import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  signal,
  computed,
  effect,
  ElementRef,
  viewChild,
  viewChildren,
  untracked,
  input,
  NgZone
} from "@angular/core";
import { IonicModule, NavController, ToastController } from "@ionic/angular";
import { StudentsPage } from "../students/containers/student-list-page/students.page";
import { StudentDetailComponent } from "../students/containers/student-detail-page/student-detail.component";
import { Platform } from "@ionic/angular";
import { StudentDto } from "../students/interfaces/student_dto";
import { Store } from "@ngrx/store";
import { loadStudents } from "../students/ngrx/students.actions";
import * as studentSelector from "../students/ngrx/students.selectors";
import { Subject, takeUntil, startWith, map } from "rxjs";
import { LessonInvoiceAddRequest } from "../office/interfaces/lesson_invoice_add_request";
import { DialogService } from "../shared/services/dialog.service";
import { groupStudentsByFirstLetter } from "../utils/GroupStudents";
import { ArchivedStudentListPageComponent } from "../students/containers/archived-student-list-page/archived-student-list-page.component";
import { NoSelectionComponent } from "../shared/components/no-selection/no-selection.component";
import { ReportGeneratorDto } from "../report-generation/interfaces/report_generator_dto";
import { selectEntitlements } from "../auth/ngrx/customer.selectors";
import { Plans } from "../shared/models/plans.enum";
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
    selector: "app-tab1",
    templateUrl: "tab1.page.html",
    styleUrls: ["tab1.page.scss"],
    imports: [
        IonicModule,
        StudentsPage,
        StudentDetailComponent,
        ArchivedStudentListPageComponent,
        NoSelectionComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class Tab1Page implements OnDestroy {
  readonly isSelection = input<boolean>(false);

  private readonly devWidth = toSignal<number>(
    this.platform.resize.pipe(
      startWith(this.platform.width()),
      map(() => { return this.platform.width() })
    )
  );
  
  readonly detailSignal = signal<"student" | "no-selection">("no-selection");
    readonly addStudentModalSignal = signal<boolean>(false);
  readonly sectionsSignal = this.store.selectSignal(studentSelector.selectActiveStudentsSections);
  readonly studentsListSignal = signal<"active-students" | "archived-students">("active-students");
  readonly studentsPageParent = signal<HTMLElement | null>(null);

  readonly isDesktop = computed(() => this.devWidth() !== undefined && this.devWidth() as number >= 700);

  archivedStudents: number = 0;
  invoice: LessonInvoiceAddRequest;
  lesson: any;
  student: StudentDto;
  customerEntitlementsSignal = this.store.selectSignal(selectEntitlements);
  studentCountSignal = this.store.selectSignal(studentSelector.selectStudentCount);
  
  private destroy$ = new Subject<void>();

  readonly studentsPage = viewChild<ElementRef>('studentsPage');
  readonly studentDetailPageComponent = viewChildren(StudentDetailComponent);

  constructor(
    private store: Store<any>,
    private platform: Platform,
    private cdr: ChangeDetectorRef,
    private navController: NavController,
    private dialogService: DialogService,
    private toastController: ToastController,
    private zone: NgZone
  ) {
    effect(() => {
      const studentDetail = this.studentDetailPageComponent();

      untracked(() => {
        if (studentDetail[0]) {
          studentDetail[0].ionViewDidEnter();
          this.cdr.markForCheck();
        }
      });
    });
  }

  ionViewWillEnter() {
    this.showStudentsPage();

    this.store.dispatch(loadStudents());
  }

  private showStudentsPage() {
    if (this.studentsPage() && this.studentsPageParent()) {
      this.zone.runOutsideAngular(() => {
        const pageEl = this.studentsPage()!.nativeElement;
        this.studentsPageParent()!.appendChild(pageEl);
        pageEl.style.display = "block";
        this.cdr.markForCheck();
      });
    }
  }

  private hideStudentsPage() {
    if (this.studentsPage() && this.studentsPageParent()) {
      this.zone.runOutsideAngular(() => {
        const pageEl = this.studentsPage()!.nativeElement;
        this.studentsPageParent()!.removeChild(pageEl);
        this.cdr.markForCheck();
      });
    }
  }

  generateReport() {
    let reportGenerator = {
      report_id: 6,
    };

    this.dialogService.openReportDialog(
      reportGenerator as ReportGeneratorDto,
      null,
      "student_list_screen"
    );
  }

  async openStudentModal() {
    if (
      !this.customerEntitlementsSignal()?.includes(Plans.premium) &&
      !this.customerEntitlementsSignal()?.includes(Plans.basic) &&
      !this.customerEntitlementsSignal()?.includes(Plans.basic_promo) &&
      !this.customerEntitlementsSignal()?.includes(Plans.premium_promo) &&
      this.studentCountSignal() >= 3
    ) {
      const toast = await this.toastController.create({
        message: "Die Anzahl der Fahrschüler ist in der Gratisversion auf 3 begrenzt.",
        duration: 5000,
        position: "bottom",
      });
      await toast.present();
      return;
    }
    this.dialogService.openNewStudentDialog();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.hideStudentsPage();
  }

  doRefresh(event: any) {
    this.store.dispatch(loadStudents());

    this.store
      .select(studentSelector.selectIsLoaded)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoaded) => {
        if (isLoaded) {
          event?.target?.complete();
          this.cdr.markForCheck();
        }
      });
  }

  addStudent() {
    this.addStudentModalSignal.set(true);
  }

  openArchivedStudents() {
    this.studentsListSignal.set("archived-students");
    this.doRefresh(null);
  }

  closeArchivedStudents() {
    this.studentsListSignal.set("active-students");
    this.doRefresh(null);
  }

  async openArchivedStudentsMobile() {
    await this.navController.navigateForward([
      "archived-students",
      { originPage: "Fahrschüler" },
    ]);
  }

  studentDetail() {
    this.detailSignal.set("student");
  }

  unselect() {
    this.detailSignal.set("no-selection");
  }

  async studentDetailMobile() {
    if (!this.isSelection()) {
      await this.navController.navigateForward([
        "student-detail",
        { originPage: "Fahrschüler" },
      ]);
    }
  }

  ionViewDidLeave() {
    this.destroy$.next();
    this.destroy$.complete();
    this.hideStudentsPage();
  }
}
